import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Loader } from '@mantine/core';
import { DrawFieldContainer } from './styled';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { RelatedToModal } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/RelatedToModal/RelatedToModal';
import { DocumentDetailsRes, DocumentFormAction } from '@dealstack/shared/lib/generated';
import { DocumentFieldView } from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/DocumentFieldView';
import { FormField } from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentFieldFilter } from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { updateDocumentResolver } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/validationSchema';

interface Props {
	editDocument: EditDocument;
	dealId: string;
	editFieldKey: string;
	setEditFieldKey: (fieldKey: string) => void;
	fieldAction: DocumentFormAction | null;
	setFieldAction: (action: DocumentFormAction | null) => void;
	dataOperations?: TableOperations<DocumentFieldFilter>;
}

export const DrawerBody = ({
	dealId,
	editDocument,
	editFieldKey,
	setEditFieldKey,
	fieldAction,
	setFieldAction,
	dataOperations,
}: Props) => {
	const queryClient = useQueryClient();
	const tCapManage = useTranslationCapManage();
	const formState = useForm<DocumentDetailsRes>({
		resolver: updateDocumentResolver,
	});

	const handleEditField = (fieldKey: string, action?: DocumentFormAction) => {
		setEditFieldKey(fieldKey);
		if (action) {
			setFieldAction(action);
		}
	};

	// Drives the "Related to" field: only a transaction-scoped document can be
	// relinked, so the backend decides that from this id.
	const transactionId = editDocument.target?.transactionId;

	const q = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
				dealId,
				documentId: editDocument.documentId,
			}),
			{ transactionId },
		],
		queryFn: ({ signal }) =>
			DocumentService.getDocument(
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
					dealId,
					documentId: editDocument.documentId,
				}),
				{ transactionId },
				signal
			),
		enabled: !!editDocument.documentId,
		meta: { errorToast: tCapManage('documents.errors.get-document') },
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		const data = q.data;
		if (!data.payload?.documentDetails) {
			return;
		}
		formState.reset(data.payload.documentDetails);
	}, [q.isSuccess, q.data]);

	const { data: document, isLoading: isDocumentsLoading } = q;

	const updateDocument = useMutation({
		mutationFn: DocumentService.updateDocument,
	});

	const onSave = async (field: FormField) => {
		const fieldData = formState.getValues(field.fieldKey as keyof DocumentDetailsRes);
		updateDocument.mutate(
			{
				dealId,
				documentId: editDocument.documentId,
				updateData: { [field.fieldKey]: fieldData },
			},
			{
				onSuccess: async () => {
					setEditFieldKey('');
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_PUT, {
								dealId,
								documentId: editDocument.documentId,
							}),
						],
					});

					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, {
								dealId,
							}) as string,
						],
					});
				},
				onError: () => {
					showToastV2({
						title: tCapManage('documents.errors.update-error'),
						state: 'error',
					});
				},
			}
		);
	};

	const onCancel = () => {
		formState.reset();
		setEditFieldKey('');
	};

	const toggleRelatedToModal = () => {
		if (fieldAction === DocumentFormAction.OPEN_LINK_MODAL) {
			setFieldAction(null);
			return;
		}
		setFieldAction(DocumentFormAction.OPEN_LINK_MODAL);
	};

	if (isDocumentsLoading) {
		return <Loader />;
	}

	return (
		<FormProvider<DocumentDetailsRes> {...formState}>
			<form onSubmit={() => onSave({ fieldKey: editFieldKey } as FormField)}>
				<DrawFieldContainer>
					<DocumentFieldView
						target={editDocument.target}
						fields={document?.payload?.meta.fields}
						formState={formState}
						dealId={dealId}
						documentId={editDocument?.documentId ?? ''}
						relatedPeople={document?.payload?.documentDetails.relatedPeople ?? []}
						documentDetails={document?.payload?.documentDetails}
						isLoading={updateDocument?.isPending}
						editFieldKey={editFieldKey}
						fieldAction={fieldAction}
						onCancel={onCancel}
						onSave={onSave}
						handleEditField={handleEditField}
						control={formState.control}
						dataOperations={dataOperations}
					/>
				</DrawFieldContainer>
			</form>
			<RelatedToModal
				dealId={dealId}
				editDocument={editDocument}
				isOpen={fieldAction === DocumentFormAction.OPEN_LINK_MODAL}
				toggleModal={toggleRelatedToModal}
			/>
		</FormProvider>
	);
};
